import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import PaymentLinkActions from '../../../../actions/PaymentLinkActions';
import PaymentLinkStore from '../../../../stores/PaymentLinkStore';
import PaymentLinkConstants from '../../../../constants/PaymentLinkConstants';
import ApiURLs from '../../../../utils/ApiURLs';
import helpers from './helpers';
import { useTranslation, Trans } from 'react-i18next';
import SettlePaymentHotelSummary from './SettlePaymentHotelSummary';
import PaymentSummary from './PaymentMethod/PaymentSummary.react';
import SettlePaymentCopyLink from './SettlePaymentCopyLink';

const SettlePaymentSendPayLaterLink = ({ booking, profile, chargeCurrency, nameState, onClose, onEdit }) => {
	const { t } = useTranslation();

	const bookingIdRef = useRef(booking?.id);

	const [paymentLinkStatus, setPaymentLinkStatus] = useState(PaymentLinkStore.getStatus(bookingIdRef.current));
	const [showError, setShowError] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		function handleStoreChange() {
			setPaymentLinkStatus(PaymentLinkStore.getStatus(bookingIdRef.current));
			const stored = PaymentLinkStore.getLink(bookingIdRef.current);
			if (stored?.token) {
				setPaymentLink(
					helpers.appendLangParam(ApiURLs.GET_PAYMENT_LINK.replace(':token', stored.token), profile),
				);
			}
		}

		PaymentLinkStore.addChangeListener(handleStoreChange);

		return () => {
			PaymentLinkStore.removeChangeListener(handleStoreChange);
		};
	}, []);

	useEffect(() => {
		if (paymentLinkStatus === PaymentLinkConstants.STATUS.BUSY) {
			setLoading(true);
			return;
		}

		setLoading(false);
		if (paymentLinkStatus === PaymentLinkConstants.STATUS.FAILED) {
			setShowError(true);
		} else if (paymentLinkStatus === PaymentLinkConstants.STATUS.SUCCESS) {
			setShowError(false);
		}
	}, [paymentLinkStatus]);

	const payment_link_expiration_exists = useMemo(() => {
		return helpers.isPaymentLinkExpirationExists(booking);
	}, [booking]);

	const token_expired = useMemo(() => {
		return helpers.isTokenExpired(booking);
	}, [booking]);

	const link_expired_time = useMemo(() => {
		const date = moment().add(48, 'hours');
		return helpers.getTimeFormatted(date);
	}, []);

	const [payment_link, setPaymentLink] = useState(() => {
		const stored = PaymentLinkStore.getLink(booking?.id);
		return stored?.token
			? helpers.appendLangParam(ApiURLs.GET_PAYMENT_LINK.replace(':token', stored.token), profile)
			: helpers.getPaymentLink(booking, profile);
	});

	const show_link = paymentLinkStatus === PaymentLinkConstants.STATUS.SUCCESS;

	const handleGenerateLink = () => {
		setLoading(true);

		PaymentLinkActions.generatePaymentLink({
			id: _.get(booking, 'payment._id'),
			booking_id: booking?.id,
			currency: chargeCurrency,
			invoice_name: nameState?.name || nameState?.otherName,
			invoice_id: nameState?.invoiceId,
			agent_pricing: _.get(booking, 'deal.agent_pricing'),
		});
	};

	const handleCloseError = () => {
		PaymentLinkActions.resetPaymentLink(booking?.id);
	};

	return (
		<div className="settle-payment-wrap">
			<div className="settle-payment">
				<PaymentSummary profile={profile} booking={booking} chargeCurrency={chargeCurrency} />

				<div className="settle-payment-generate-link">
					<div className="description">{t('Bookings.SettlePayment.YouCanSendYourClient')}</div>

					{payment_link_expiration_exists && token_expired && (
						<div className="expired-message">
							<Trans i18nKey="PaymentLinkExpired"></Trans>
						</div>
					)}

					<div className="copy-link-wrap">
						{showError && (
							<div className="copy-link full-width">
								<div className="show-error" onClose={handleCloseError}>
									{t('SomethingWentWrong')}
								</div>
								<Button className="generate-button" variant="contained" onClick={handleGenerateLink}>
									{loading ? <CircularProgress size="20px" /> : t('TryAgain')}
								</Button>
							</div>
						)}
						{!showError &&
							(show_link ? (
								<SettlePaymentCopyLink link={payment_link} />
							) : (
								<Button
									className="generate-button full-width"
									variant="contained"
									onClick={handleGenerateLink}
								>
									{loading ? <CircularProgress size="20px" /> : t('GenerateLink')}
								</Button>
							))}
					</div>

					<div className="expiration">{t('LinkAvailability', { expiration: link_expired_time })}</div>
				</div>

				<div className="actions">
					{show_link && <Button variant="outlined" fullWidth onClick={() => {
						onEdit();
						PaymentLinkActions.resetPaymentLink(booking?.id);
					}}>
						{t('EditPrice')}
					</Button>}

					<Button variant="contained" fullWidth onClick={onClose}>
						{t('Bookings.SettlePayment.Finish')}
					</Button>
				</div>
			</div>
			<SettlePaymentHotelSummary booking={booking} />
		</div>
	);
};

export default SettlePaymentSendPayLaterLink;
